// iter574 — Extracted from BankStatementsPage.jsx (Transactions tab).
// Receives the loaded statement detail + setters for the inline dictionary
// edit and manual-party-link dialogs (those dialogs stay at the page level
// because they are also opened from the Parties tab).
import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StatementCodeInbox from '@/components/banking/StatementCodeInbox';
import api from '@/lib/api';
import { toast } from 'sonner';

const CAT_LABELS = { pos_sales: 'POS Sales', pos_fees: 'POS Fees', bank_fees: 'Bank Fees', vat_fees: 'VAT on Fees', internal_transfer: 'Internal Transfers', incoming_transfer: 'Incoming Transfers', outgoing_transfer: 'Outgoing Transfers', sadad_payment: 'SADAD Bills', sadad_refund: 'SADAD Refund', iqama_renewal: 'Iqama Renewal', salary: 'Salary', vat: 'VAT', refund: 'Refund', other_sales: 'Other Sales', other: 'Other' };

const MATCH_TYPE_LABELS = { expense: 'Bill', supplier_payment: 'Supplier Payment', sale: 'Sale', customer_payment: 'Customer Payment', salary_payment: 'Salary Payment' };

export function TransactionsTab({
  detail,
  onChanged,
  onOpenDict,
  onOpenLink,
  onFindMatch,
}) {
  // Branch-aware smart suggestions — reuses the recon-cockpit matcher.
  // Only strong candidates (exact amount or ref/score >= 80) are shown.
  const [suggestions, setSuggestions] = useState({});
  const [accepting, setAccepting] = useState(null);
  useEffect(() => {
    if (!detail?.id) { setSuggestions({}); return; }
    api.get(`/recon-cockpit/unmatched?statement_id=${detail.id}&limit=500`)
      .then((r) => {
        const map = {};
        (r.data?.rows || []).forEach((row) => {
          const b = row.best;
          if (b && (b.amt_diff === 0 || b.score >= 80)) map[row.txn_index] = b;
        });
        setSuggestions(map);
      })
      .catch(() => setSuggestions({}));
  }, [detail]);

  const acceptSuggestion = async (i, b) => {
    setAccepting(i);
    try {
      await api.post(`/bank-statements/${detail.id}/manual-match`, { txn_index: i, match_type: b.type, match_id: b.id });
      toast.success(`Linked to ${b.party_name || MATCH_TYPE_LABELS[b.type] || b.type} — marked as paid`);
      setSuggestions((s) => { const n = { ...s }; delete n[i]; return n; });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    } finally {
      setAccepting(null);
    }
  };

  if (!detail) return null;
  return (
    <div data-testid="transactions-content-inner">
      <StatementCodeInbox
        statementId={detail.id}
        bankAccountId={detail.bank_account_id}
        onChanged={onChanged}
      />
      <div className="max-h-[600px] overflow-y-auto border rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b sticky top-0 bg-muted/50 z-10"><th className="text-left p-2 text-xs font-medium">Date</th><th className="text-left p-2 text-xs font-medium">Category</th><th className="text-left p-2 text-xs font-medium">Description</th><th className="text-right p-2 text-xs font-medium">In</th><th className="text-right p-2 text-xs font-medium">Out</th></tr></thead>
            <tbody>
              {detail.transactions?.map((t, i) => (
                <tr key={i} className="border-b hover:bg-muted/50 text-xs">
                  <td className="p-2 whitespace-nowrap">{t.date}</td>
                  <td className="p-2">
                    <Select
                      value={t.category || 'other'}
                      onValueChange={async (v) => {
                        try {
                          await api.put(`/bank-statements/${detail.id}/transactions/${i}/category`, { category: v });
                          toast.success(`Tagged as ${CAT_LABELS[v] || v}`);
                          onChanged();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to tag');
                        }
                      }}
                    >
                      <SelectTrigger className={`h-6 text-[10px] px-1.5 rounded-md border-0 ${t.manual_category ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300' : 'bg-secondary text-secondary-foreground'} hover:ring-1 hover:ring-primary/40`} data-testid={`txn-row-category-${i}`}>
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent align="start" className="text-xs">
                        <SelectItem value="pos_sales">POS Sales</SelectItem>
                        <SelectItem value="other_sales">Other Sales</SelectItem>
                        <SelectItem value="pos_fees">POS Fees</SelectItem>
                        <SelectItem value="vat_fees">VAT Fees</SelectItem>
                        <SelectItem value="bank_fees">Bank Fees</SelectItem>
                        <SelectItem value="refund">Refund</SelectItem>
                        <SelectItem value="incoming_transfer">Incoming Transfer</SelectItem>
                        <SelectItem value="outgoing_transfer">Outgoing Transfer</SelectItem>
                        <SelectItem value="salary">Salary</SelectItem>
                        <SelectItem value="iqama_renewal">Iqama Renewal</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {t.manual_category && t.original_category && t.original_category !== t.category && (
                      <div className="text-[9px] text-muted-foreground mt-0.5" title={`Auto-detected as ${t.original_category}, manually changed${t.tagged_by ? ' by ' + t.tagged_by : ''}`}>
                        was: {CAT_LABELS[t.original_category] || t.original_category}
                      </div>
                    )}
                  </td>
                  <td className="p-2 max-w-md">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {t.decoded_label ? (
                        <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const m = (t.description || '').match(/[A-Z][A-Z0-9_\-/]{2,}/);
                            onOpenDict({ code: (m ? m[0] : t.decoded_label).toUpperCase(), label: t.decoded_label });
                          }}
                          title={`Auto-decoded from "${(t.description || '').slice(0, 80)}${(t.description || '').length > 80 ? '…' : ''}" — click to edit the dictionary`}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-200 hover:border-sky-300 transition-colors whitespace-nowrap cursor-pointer"
                          data-testid={`stmt-decoded-badge-${i}`}>
                          {t.decoded_label}
                        </button>
                        {t.decoded_alternates && t.decoded_alternates.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const alts = t.decoded_alternates.map(a => `${a.code} → ${a.label}`).join('\n');
                              alert(`This row could also have matched:\n\n${alts}\n\nThe leftmost match in the description wins. Edit a label in "Your saved labels" if you'd like a different one to win.`);
                            }}
                            title={`Conflict: ${t.decoded_alternates.length} other custom label${t.decoded_alternates.length === 1 ? '' : 's'} also match this row`}
                            className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 cursor-pointer"
                            data-testid={`stmt-conflict-badge-${i}`}>
                            !
                          </button>
                        )}
                        </>
                      ) : (
                        (() => {
                          const m = (t.description || '').match(/[A-Z][A-Z0-9_\-/]{2,}/);
                          if (!m) return null;
                          return (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onOpenDict({ code: m[0].toUpperCase(), label: '' }); }}
                              title={`Teach Smarto what "${m[0]}" means`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors whitespace-nowrap cursor-pointer"
                              data-testid={`stmt-label-cta-${i}`}>
                              + Label
                            </button>
                          );
                        })()
                      )}
                      <span className="truncate" title={t.description}>{t.description}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      {t.link_id && t.link_name ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenLink({ index: i, description: t.description, link_type: t.link_type, link_id: t.link_id, link_name: t.link_name });
                          }}
                          title={`Linked to ${t.link_type}: ${t.link_name}. Click to change.`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                          data-testid={`txn-linked-${i}`}
                        >
                          ↪ {t.link_type}: {t.link_name}
                        </button>
                      ) : (
                        <>
                          {suggestions[i] && (
                            <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`txn-suggestion-${i}`}>
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-800 border border-sky-200"
                                title={suggestions[i].why}
                              >
                                Found matching {MATCH_TYPE_LABELS[suggestions[i].type] || suggestions[i].type} for Branch {suggestions[i].branch || '—'} · {suggestions[i].party_name}
                              </span>
                              <button
                                type="button"
                                disabled={accepting === i}
                                onClick={(e) => { e.stopPropagation(); acceptSuggestion(i, suggestions[i]); }}
                                className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                data-testid={`txn-suggestion-accept-${i}`}
                              >
                                {accepting === i ? 'Linking…' : 'Accept'}
                              </button>
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenLink({ index: i, description: t.description });
                            }}
                            title="Manually link this transaction to a supplier / customer / employee / expense"
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground border border-border hover:bg-foreground hover:text-background transition-colors"
                            data-testid={`txn-link-cta-${i}`}
                          >
                            + Link
                          </button>
                          {onFindMatch && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onFindMatch(i); }}
                              title="Find candidates from sales / expenses / supplier payments — ranked by amount, date, party, branch & reference"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-violet-50 text-violet-800 border border-violet-200 hover:bg-violet-100 transition-colors"
                              data-testid={`txn-find-match-${i}`}
                            >
                              ⓘ Find match
                            </button>
                          )}
                        </>
                      )}
                      {/* iter836 — Subtle pill when this transaction was
                          auto-settled against a pass-through advance. */}
                      {t.advance_reconciled_id && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-teal-50 text-teal-800 border border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800"
                          title={`Auto-settled from pass-through advance${t.advance_reconciled_at ? ' on ' + String(t.advance_reconciled_at).slice(0, 10) : ''}`}
                          data-testid={`txn-advance-pill-${i}`}
                        >
                          ⇋ advance settled{t.advance_reconciled_person ? ` → ${t.advance_reconciled_person}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-right text-success">{t.credit > 0 ? t.credit.toFixed(2) : ''}</td>
                  <td className="p-2 text-right text-error">{t.debit > 0 ? t.debit.toFixed(2) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
